import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1B3A6B', // Deep government blue
      light: '#4A5F8F',
      dark: '#0F2844',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#2D8659', // Government green
      light: '#52A876',
      dark: '#1F5A3D',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#FFA500', // Amber
      light: '#FFB833',
      dark: '#E67E00',
    },
    error: {
      main: '#D32F2F',
      light: '#E57373',
      dark: '#B71C1C',
    },
    success: {
      main: '#388E3C',
      light: '#66BB6A',
      dark: '#2E7D32',
    },
    info: {
      main: '#1976D2',
      light: '#42A5F5',
      dark: '#1565C0',
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
      disabled: '#BDBDBD',
    },
    divider: '#E0E0E0',
  },

  typography: {
    fontFamily: '"Roboto", "Helvetica Neue", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
    },
  },

  shape: {
    borderRadius: 8,
  },

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '0.95rem',
          padding: '10px 24px',
          borderRadius: 8,
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
          },
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          padding: '14px 32px',
          fontSize: '1rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            backgroundColor: '#F5F5F5',
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: 'rgba(27, 58, 107, 0.04)',
          },
        },
      },
    },
  },
});

/**
 * The admin dashboard (AdminDashboardPage and everything under it — Dashboard,
 * Officers, Locations, Reports, Gaps, Verify, Exports) is styled dark throughout
 * via manual `sx` overrides, but the app only ever had one, *light*, MUI theme.
 * Every element that didn't set its own `color` explicitly fell back to the
 * light theme's `text.primary` (#212121, near-black) — invisible against the
 * manually-dark backgrounds. Rather than hunting every missing `color` prop
 * (Select/MenuItem popups, dialog content, etc.), this theme makes MUI's own
 * defaults (Paper, Menu, Dialog, text.primary/secondary) dark to match, so
 * anything that forgets to set a color renders visibly instead of invisibly.
 * Wrap the admin dashboard subtree in <ThemeProvider theme={darkTheme}> —
 * everything else (login, the officer PWA fallback) keeps the light theme.
 */
export const darkTheme = createTheme({
  ...theme,
  palette: {
    mode: 'dark',
    primary: theme.palette.primary,
    secondary: theme.palette.secondary,
    warning: theme.palette.warning,
    error: theme.palette.error,
    success: theme.palette.success,
    info: theme.palette.info,
    background: {
      default: '#0f1419',
      paper: '#1a2332',
    },
    text: {
      primary: '#ffffff',
      secondary: '#8b95a5',
      disabled: '#5a6472',
    },
    divider: '#2a3444',
  },
  components: {
    ...theme.components,
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // MUI's dark-mode Paper elevation overlay looks off against these flat panels
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: '#252d3d' },
          '&.Mui-selected': { backgroundColor: '#252d3d' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: '#2a3444' },
      },
    },
  },
});

/**
 * USAJILI dashboard theme — same dark base as darkTheme but with
 * #00D084 (SDICS admin green) as primary so chips/links/buttons
 * are visible on the dark #0f1419 background.
 */
export const usajiliTheme = createTheme({
  ...darkTheme,
  typography: {
    ...darkTheme.typography,
    fontFamily: '"Roboto", "Helvetica Neue", "Arial", sans-serif',
  },
  palette: {
    ...darkTheme.palette,
    primary: {
      main: '#00D084',
      light: '#33DA9B',
      dark: '#00A367',
      contrastText: '#0f1419',
    },
    secondary: {
      main: '#FFA500',
      light: '#FFB833',
      dark: '#E67E00',
      contrastText: '#0f1419',
    },
  },
  components: {
    ...darkTheme.components,
    // Override the light theme's white table-header background — dark mode needs dark header
    MuiTable: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            backgroundColor: '#0f1419',
            color: '#ffffff',
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: 'rgba(0, 208, 132, 0.05)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: '#e8e3dc',
          borderColor: '#2a3444',
        },
        head: {
          color: '#ffffff',
          backgroundColor: '#0f1419',
          borderBottom: '1px solid #2a3444',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: '#2a3444' },
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00D084' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00D084' },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: '#8b95a5', '&.Mui-focused': { color: '#00D084' } },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500 },
        outlinedPrimary: { borderColor: 'rgba(0,208,132,0.5)', color: '#00D084' },
        outlinedSecondary: { borderColor: 'rgba(255,165,0,0.5)', color: '#FFA500' },
      },
    },
    MuiLink: {
      styleOverrides: { root: { color: '#00D084' } },
    },
  },
});

/**
 * USAJILI light theme — high-contrast alternative to usajiliTheme for users
 * who find the dark dashboard hard to read. Darker green primary (#00875A)
 * keeps text/buttons above WCAG AA contrast on white.
 */
export const usajiliLightTheme = createTheme({
  ...theme,
  typography: {
    ...theme.typography,
    fontFamily: '"Roboto", "Helvetica Neue", "Arial", sans-serif',
  },
  palette: {
    ...theme.palette,
    mode: 'light',
    primary: {
      main: '#00875A',
      light: '#2FA67B',
      dark: '#006644',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#B45309',
      light: '#D97706',
      dark: '#92400E',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F3F5F8',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#111827',
      secondary: '#4B5563',
      disabled: '#9CA3AF',
    },
    divider: '#D5DAE1',
  },
  components: {
    ...theme.components,
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            backgroundColor: '#EEF1F5',
            color: '#111827',
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: 'rgba(0, 135, 90, 0.06)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { color: '#111827', borderColor: '#E3E7ED' },
        head: { color: '#111827', backgroundColor: '#EEF1F5', borderBottom: '1px solid #D5DAE1' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: '#C3CAD4' },
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00875A' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00875A' },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: '#4B5563', '&.Mui-focused': { color: '#00875A' } },
      },
    },
  },
});
